import { Button, cn, formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate } from '@tanstack/react-router';

import { sqlUtcToIso } from '../../lib/sqlTimestamp.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { MoreMenu, type MoreMenuItem } from '../_ops/MoreMenu.js';
import { SkeletonRows } from '../_ops/SkeletonRows.js';
import { EstimateStatusBadge } from './components/EstimateStatusBadge.js';
import { EstimateSummaryCards } from './components/EstimateSummaryCards.js';
import { EstimatesEmptyState } from './components/EstimatesEmptyState.js';
import { EstimatesTable } from './components/EstimatesTable.js';
import { StatusFilterPills } from './components/StatusFilterPills.js';
import type { EstimateListItemDto, EstimatesResponse } from './contractsMirror.js';
import { NeedsPricingPanel } from './NeedsPricingPanel.js';
import type { EstimatesSearch } from './searchParams.js';

/**
 * The estimates list on the locked Velocity brand, uplifted to the P22 redesign
 * grammar (D-04 re-skin — functionality UNCHANGED). Page wrapper + header (title,
 * subtitle, archived toggle + the single primary "+ New Estimate" CTA) + the
 * 4-card summary stat grid + the status-filter pill row + the estimates table —
 * or the empty state. The coarse endpoint owns all aggregation + the status
 * counts; this view only renders the returned payload (no hook/endpoint change).
 *
 * Redesign grammar added here (UI-SPEC §4/§5), all presentation-only:
 * - **Responsive table→card**: the semantic `<table>` (`EstimatesTable`) shows at
 *   `md+`; below `md` the rows reflow to stacked label:value record cards with a
 *   `_ops/MoreMenu` row menu (View, plus Edit for draft/ready — navigation only).
 * - **Skeleton loading**: an optional `loading` flag renders `_ops/SkeletonRows`
 *   shimmer rows; it defaults off so the existing Route render is unchanged.
 *
 * The archived toggle + each status pill are `Link`s that flip the typed search
 * params. The "+ New Estimate" header CTA + the active status pill are the only
 * orange-accent surfaces here. Every color is a locked token (BRAND-06).
 */

export interface EstimatesViewProps {
  data: EstimatesResponse;
  search: EstimatesSearch;
  /** Renders skeleton shimmer rows during a background refetch. Defaults off. */
  loading?: boolean;
}

export function EstimatesView({ data, search, loading = false }: EstimatesViewProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const fmt = useMoneyFormatters();
  const navigate = useNavigate();

  const estimates = data.estimates ?? [];
  const statusCounts = data.statusCounts ?? {};
  const totalEstimates = Number(data.totalEstimates ?? estimates.length);
  const pipelineValue = Number(data.pipelineValue ?? 0);
  const draftReadyCount = Number(data.draftReadyCount ?? 0);
  const approvedCount = Number(data.approvedCount ?? 0);
  const showArchived = Boolean(search.show_archived);
  const hasData = estimates.length > 0;

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {showArchived ? t('estimates:archivedTitle') : t('estimates:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {showArchived ? t('estimates:archivedSubtitle') : t('estimates:subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/estimates"
            search={showArchived ? {} : { show_archived: true }}
            className={cn(
              'inline-flex h-11 items-center rounded-md border border-border bg-card px-4 font-medium text-foreground text-sm transition-colors',
              'hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
          >
            {showArchived
              ? t('estimates:actions.backToActive')
              : t('estimates:actions.archivedEstimates')}
          </Link>
          {showArchived ? null : (
            <Button asChild>
              <Link to="/estimates/new">{t('estimates:actions.newEstimate')}</Link>
            </Button>
          )}
        </div>
      </header>

      {/* Visits the crew has been out to that nobody has priced yet. Renders nothing when the
          queue is empty, so it reads as a signal rather than page furniture. Placed above the
          summary cards because it is work waiting on a person, not a number to glance at. */}
      <NeedsPricingPanel />

      <EstimateSummaryCards
        totalEstimates={totalEstimates}
        pipelineValue={pipelineValue}
        draftReadyCount={draftReadyCount}
        approvedCount={approvedCount}
        fmt={fmt}
      />

      <StatusFilterPills search={search} statusCounts={statusCounts} totalCount={totalEstimates} />

      {loading ? (
        <section className="rounded-md border border-border bg-card p-4">
          <SkeletonRows rows={6} columns={4} label={t('estimates:title')} />
        </section>
      ) : hasData ? (
        <>
          {/* Semantic table at md+; stacked record cards below md (no h-scroll on phones). */}
          <div className="hidden md:block">
            <EstimatesTable estimates={estimates} fmt={fmt} />
          </div>
          <ul className="grid gap-3 md:hidden">
            {estimates.map((est) => (
              <li key={est.id}>
                <EstimateRecordCard
                  estimate={est}
                  formatCurrency={fmt.formatCurrency}
                  locale={locale}
                  onView={() => navigate({ to: '/estimates/$id', params: { id: String(est.id) } })}
                  onEdit={() =>
                    navigate({ to: '/estimates/$id/edit', params: { id: String(est.id) } })
                  }
                />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <EstimatesEmptyState showArchived={showArchived} />
      )}
    </div>
  );
}

/** Draft/ready estimates are still editable (legacy parity, mirrors EstimatesTable). */
function isEditable(status: EstimateListItemDto['status']): boolean {
  return status === 'draft' || status === 'ready';
}

/**
 * One estimate as a stacked label:value record card (UI-SPEC §5 responsive
 * table→card). The estimate number links to the detail route; the `_ops/MoreMenu`
 * exposes View (and Edit for draft/ready) — navigation only, no list-side
 * mutation. Same data + locked tokens as `EstimatesTable`.
 */
function EstimateRecordCard({
  estimate,
  formatCurrency,
  locale,
  onView,
  onEdit,
}: {
  estimate: EstimateListItemDto;
  formatCurrency: (value: number) => string;
  locale: string;
  onView: () => void;
  onEdit: () => void;
}) {
  const { t } = useTranslation();

  const menuItems: MoreMenuItem[] = [
    { key: 'view', label: t('estimates:actions.view'), onSelect: onView },
  ];
  if (isEditable(estimate.status)) {
    menuItems.push({ key: 'edit', label: t('estimates:actions.editShort'), onSelect: onEdit });
  }

  return (
    <div className="rounded-md border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-3">
        <Link
          to="/estimates/$id"
          params={{ id: String(estimate.id) }}
          className="font-mono font-semibold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {estimate.estimateNumber || t('estimates:table.unnamedEstimate', { id: estimate.id })}
        </Link>
        <MoreMenu label={t('estimates:actions.view')} items={menuItems} />
      </div>
      <div className="mt-0.5 text-muted-foreground text-xs">
        {estimate.customerName || t('estimates:table.noCustomer')}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3">
        <EstimateStatusBadge status={estimate.status} />
        <span className="font-mono font-medium text-foreground text-sm">
          {formatCurrency(estimate.total || 0)}
        </span>
      </div>
      <dl className="mt-3 border-border border-t pt-3">
        <div className="flex flex-col gap-0.5">
          <dt className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
            {t('estimates:table.updated')}
          </dt>
          <dd className="font-mono text-foreground text-sm">
            {estimate.updated
              ? formatUiDate(sqlUtcToIso(estimate.updated), locale, { dateStyle: 'medium' })
              : '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
}
